<script setup lang="ts">
import { ref, watch } from 'vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import VueDatePicker from '@vuepic/vue-datepicker'
import Button from '../Utils/Button.vue'
import { debounce } from 'lodash-es'
import { computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import { router } from '@inertiajs/vue3'
import { notify } from '@kyvg/vue3-notification'
import { routeType } from '@/types/route'
import PureMultiselect from '../Pure/PureMultiselect.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowAltFromRight, faArrowAltFromLeft } from '@far'
import { library } from '@fortawesome/fontawesome-svg-core'
import InformationIcon from '../Utils/InformationIcon.vue'
library.add(faArrowAltFromRight, faArrowAltFromLeft)

interface FeeAdditionalItem {
    id: number
    date: string | Date
    type: string
    payment: string
    label: string
    amount: number
}

const props = withDefaults(defineProps<{
    feeAdditional: FeeAdditionalItem[]
    routeUpdate?: routeType
    previousMonthYear?: {
        month: number
        year: number
    }
    calculating?: {
        invoiceCashPaidOff: number
        totalAmountOrdersByCash: number
    }
}>(), {
    feeAdditional: () => []
})


const enumTipeFee = [
    { label: 'Pemasukan', value: 'income' },
    { label: 'Pengeluaran', value: 'expense' },
]
const enumPaymentFee = [
    { label: 'Cash', value: 'cash' },
    { label: 'Transfer', value: 'transfer' },
]



// Section: Handle dirty fields and changes
const originalData = ref<FeeAdditionalItem[]>(JSON.parse(JSON.stringify(props.feeAdditional)))
type DirtyFields = Partial<Record<keyof FeeAdditionalItem, boolean>>
const dirtyMap = ref<Record<number, DirtyFields>>({})
const isEqual = (a: any, b: any) => {
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime()
    }
    return a === b
}
const onRowChange = debounce((row: FeeAdditionalItem) => {
    const original = originalData.value.find(i => i.id === row.id)
    if (!original) return

    const changedFields: DirtyFields = {};

    (['date', 'label', 'amount', 'type', 'payment'] as (keyof FeeAdditionalItem)[]).forEach(key => {
        if (!isEqual(original[key], row[key])) {
        changedFields[key] = true
        }
    })

    if (Object.keys(changedFields).length > 0) {
        dirtyMap.value[row.id] = changedFields
    } else {
        delete dirtyMap.value[row.id]
    }
}, 400)
// Method: to check if a field is dirty (if dirty will be green)
const isDirty = (id: number, field: keyof FeeAdditionalItem) => {
    return !!dirtyMap.value[id]?.[field]
}
// Computed: to check if there are any changes (if true, enable Save Changes button)
const hasChanges = computed(() => {
    return Object.keys(dirtyMap.value).length > 0
})




// Method to add a new blank cost row
const addRow = () => {
    console.log('qq', props.feeAdditional)
    const newId =
        props.feeAdditional.length > 0
        ? Math.max(...props.feeAdditional.map(i => i.id)) + 1
        : 1

    const newItem: FeeAdditionalItem = {
        id: newId,
        date: new Date().toISOString().slice(0, 10),
        type: 'income',
        payment: '',
        label: '',
        amount: 0
    }

    props.feeAdditional.push(newItem)

    dirtyMap.value[newId] = {
        date: true,
        label: true,
        amount: true,
        type: true,
        payment: true
    }
}

const isLoadingSubmit = ref(false)
const onUpdateProfit = () => {
    if (!props.routeUpdate) {
        console.log('props.routeUpdate', props.routeUpdate)
        notify({
            title: 'Terjadi kesalahan',
            text: 'Route Update tidak ditemukan',
            type: 'error',
        })
    }

    router.patch(
        route(props.routeUpdate.name, props.routeUpdate.parameters || {}),
        {
            fee_additional: props.feeAdditional,
        },
        {
            preserveScroll: true,
            onSuccess: () => {
                notify({
                    title: 'Perubahan disimpan',
                    text: 'Fee tambahan berhasil diperbarui.',
                    type: 'success',
                })
            },
            onError: (e) => {
                notify({
                    title: 'Terjadi kesalahan',
                    text: 'Gagal menyimpan perubahan. Coba lagi..',
                    type: 'error',
                })
            },
            onFinish: () => {
                isLoadingSubmit.value = false
            }

        }
    )
}
const onSubmitChange = async () => {
    if (!compPreviousMonthFormat.value) return

    isLoadingSubmit.value = true

    if (props.routeUpdate) {
        onUpdateProfit()
    } else {
        router.post(
            route('master.profits.generate', {
                since: compPreviousMonthFormat.value
            }),
            { },
            {
                except: ['fee_additional'],
                onSuccess: () => {
                    console.log('props.routeUpdate', props.routeUpdate)
                    onUpdateProfit()
                },
                onError: (e) => {
                    notify({
                        title: 'Terjadi kesalahan',
                        text: 'Gagal generate bulan baru. Coba lagi..',
                        type: 'error',
                    })
                }
            }
        )
        
    }

    dirtyMap.value = {}
    originalData.value = JSON.parse(JSON.stringify(props.feeAdditional))

    // isLoadingSubmit.value = false
}

const onDeleteCost = (id: number) => {
    const idx = props.feeAdditional.findIndex(item => item.id === id)
    if (idx !== -1) {
        props.feeAdditional.splice(idx, 1)
        dirtyMap.value[id] = {}
    }
}


// Section: Datepicker
const selectedYM = ref({
    month: parseInt((route().params?.year_month)?.substring(4, 6), 10) || (new Date().getMonth() + 1),
    year: parseInt((route().params?.year_month)?.substring(0, 4), 10) || new Date().getFullYear()
})
const compPreviousMonthFormat = computed(() => {
    if (props.previousMonthYear) {
        return `${props.previousMonthYear.year}${String(props.previousMonthYear.month).padStart(2, '0')}`
    }

    return `${selectedYM.value.year}${String(selectedYM.value.month).padStart(2, '0')}`
})
const onChangeDate = (month_year: typeof selectedYM.value) => {
    console.log('changeDate', month_year)
    router.reload({
        data: { 
            year_month: `${month_year.year}${(month_year.month + 1).toString().padStart(2, '0')}`
        },  // Sent to url parameter (?tab=showcase, ?tab=menu)
        preserveScroll: true,
        onSuccess: () => {
            selectedYM.value.month = Number((month_year.month + 1).toString().padStart(2, '0')),
            selectedYM.value.year = month_year.year
            dirtyMap.value = {}
        },
        onError: (e) => {
            // console.log('eeerr', e)
            notify({
                title: 'Sepertinya ada kesalahan',
                text: 'Gagal mengubah bulan. Coba lagi..',
                type: 'error',
            })
        }
    })
}


const compCalculatedCashFlow = computed(() => {
    let total = 0
    props.feeAdditional.forEach(item => {
        if (item.type === 'income') {
            total += item.amount
        } else if (item.type === 'expense') {
            total -= item.amount
        }
    })
    return total
})

const compCalculatedCashOnHand = computed(() => {
    let total = 0
    props.feeAdditional.forEach(item => {
        if (item.payment === 'cash') {
            if (item.type === 'income') {
                total += item.amount
            } else if (item.type === 'expense') {
                total -= item.amount
            }
        }
    })

    return total
})
const compCalculatedTransfer = computed(() => {
    let total = 0
    props.feeAdditional.forEach(item => {
        if (item.payment === 'transfer') {
            if (item.type === 'income') {
                total += item.amount
            } else if (item.type === 'expense') {
                total -= item.amount
            }
        }
    })

    return total
})


watch(() => props.previousMonthYear, (val, newVal) => {
    if (JSON.stringify(val) === JSON.stringify(newVal)) {
        return
    } else if (props.previousMonthYear) {
        onChangeDate(props.previousMonthYear)
    }
})

defineExpose({
    compCalculatedCashOnHand,
    compCalculatedTransfer
})
</script>


<template>
    <div class="">
        <div v-if="!props.previousMonthYear">
            Bulan: <span class="text-gray-700 font-bold">{{ new Date(selectedYM.year, selectedYM.month - 1).toLocaleString('id-ID', { month: 'long', year: 'numeric' }) }}</span>
            <div class="w-44">
                <VueDatePicker
                    :clearable="false"
                    autoApply
                    :modelValue="{
                        month: selectedYM.month - 1,
                        year: selectedYM.year
                    }"
                    @update:modelValue="(e) => (console.log(e), onChangeDate(e))"
                    month-picker
                />
            </div>
        </div>

        <div class="mt-4 border border-gray-300 p-4 rounded-lg">
            <div class="border-b border-dashed border-gray-300 pb-2 flex justify-between items-center">
                <div class="w-full flex justify-between gap-x-24 gap-y-3">
                    <div v-if="calculating?.invoiceWhichCash" class="h-fit bg-gray-100 p-2 rounded-md">
                        <div class="opacity-70 italic font-normal">
                            Cash dari Invoice <InformationIcon
                                class="inline-block ml-1"
                                information="Total penjualan dari invoice yang dibayar dengan metode 'cash' pada bulan ini."
                            />
                        </div>
                        <div>
                            <div class="text-lg font-semibold tabular-nums text-primary"
                                :class="calculating.invoiceWhichCash >= 0 ? 'text-green' : 'text-red-500'"
                            >
                                Total: {{ useFormatCurrency(calculating.invoiceWhichCash || 0) }}
                            </div>
                            <div class="text-lg font-semibold tabular-nums text-primary">
                                Lunas: {{ useFormatCurrency(calculating?.invoiceCashPaidOff || 0) }}
                            </div>
                            <div class="text-lg font-semibold tabular-nums text-primary"
                                :class="calculating.invoiceWhichCash >= 0 ? 'text-green' : 'text-red-500'"
                            >
                                Tersedia: {{ useFormatCurrency((calculating.invoiceCashPaidOff || 0) + (compCalculatedCashOnHand || 0)) }}
                            </div>
                        </div>
                    </div>

                    <div v-if="calculating?.invoiceWhichTransfer" class="h-fit bg-gray-100 p-2 rounded-md">
                        <div class="opacity-70 italic font-normal">
                            Transfer dari Invoice <InformationIcon
                                class="inline-block ml-1"
                                information="Total penjualan dari invoice yang dibayar dengan metode 'transfer' pada bulan ini."
                            />
                        </div>
                        <div>
                            <div class="text-lg font-semibold tabular-nums text-primary"
                                :class="calculating.invoiceWhichTransfer >= 0 ? 'text-green' : 'text-red-500'"
                            >
                                Total: {{ useFormatCurrency(calculating.invoiceWhichTransfer || 0) }}
                            </div>
                            <div class="text-lg font-semibold tabular-nums text-primary">
                                Lunas: {{ useFormatCurrency(calculating?.invoiceTransferPaidOff || 0) }}
                            </div>
                            <div class="text-lg font-semibold tabular-nums text-primary"
                                :class="calculating.invoiceWhichTransfer >= 0 ? 'text-green' : 'text-red-500'"
                            >
                                Tersedia: {{ useFormatCurrency((calculating.invoiceTransferPaidOff || 0) + (compCalculatedTransfer || 0)) }}
                            </div>
                        </div>
                    </div>

                    <div class="w-fit grid grid-cols-2 items-center gap-x-24 border p-2 bg-cyan-50">
                        <div>
                            <div class="opacity-70 italic font-normal">Kalkulasi arus:</div>
                            <div class="text-lg font-semibold tabular-nums text-primary"
                                :class="compCalculatedCashFlow >= 0 ? 'text-green' : 'text-red-500'"
                            >
                                {{
                                    useFormatCurrency(compCalculatedCashFlow)
                                }}
                            </div>
                        </div>
                        <div>
                            <div class="opacity-70 italic font-normal">
                                Kalkulasi tipe cash <InformationIcon
                                    class="inline-block ml-1"
                                    information="Menghitung pemasukan dan pengeluaran dengan metode pembayaran 'cash'."
                                />:
                            </div>
                            <div class="text-lg font-semibold tabular-nums text-primary"
                                :class="compCalculatedCashOnHand >= 0 ? 'text-green' : 'text-red-500'"
                            >
                                {{
                                    useFormatCurrency(compCalculatedCashOnHand)
                                }}
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <div class="mt-2 flex justify-end">
                <Button
                    @click="onSubmitChange"
                    icon="fad fa-save"
                    label="Simpan perubahan"
                    :loading="isLoadingSubmit"
                    :disabled="!hasChanges"
                    type="success"
                />
            </div>
            
            <div class="max-h-96 my-4 w-full">
                <DataTable
                    :value="feeAdditional"
                    dataKey="id"
                    :pt="{
                        tableContainer: {
                            style: {
                                overflow: 'unset'
                            }
                        }
                    }"
                >
                    <Column field="date" header="Tanggal">
                        <template #body="{ data, field }">
                            <VueDatePicker
                                v-model="data[field]"
                                model-type="yyyy-MM-dd"
                                auto-apply
                                @update:modelValue="() => onRowChange(data)"
                                :class="isDirty(data.id, field) ? '!w-fit !border !border-green-500 !bg-green-100' : ''"
                            >
                                <template #trigger>
                                    <Button
                                        :label="data[field] ?? 'No date'"
                                        type="tertiary"
                                    />
                                </template>
                            </VueDatePicker>
                        </template>
                    </Column>

                    <!-- Column: Label -->
                    <Column field="label" header="Label">
                        <template #body="{ data, field }">
                            <InputText
                            v-model="data[field]"
                            @update:modelValue="() => onRowChange(data)"
                            :pt="{
                                root: {
                                class: isDirty(data.id, field)
                                    ? '!border !border-green-500 !bg-green-100'
                                    : ''
                                }
                            }"
                            />
                        </template>
                    </Column>

                    <!-- Column: Tipe -->
                    <Column field="type" header="Tipe">
                        <template #body="{ data, field }">
                            <div class="w-44">
                                <PureMultiselect
                                    v-model="data[field]"
                                    :options="enumTipeFee"
                                    labelProp="label"
                                    required
                                    valueProp="value"
                                    placeholder="Pilih pemasukan/pengeluaran"
                                    @update:modelValue="() => onRowChange(data)"
                                    :pt="{
                                        root: {
                                        class: isDirty(data.id, field)
                                            ? '!border !border-green-500 !bg-green-100'
                                            : ''
                                        }
                                    }"
                                >
                                    <template #label="{ value }">
                                        <div class="text-left mr-auto pl-3" :class="value.value === 'income' ? 'text-green-500' : ''">
                                            {{ value.label }}
                                            <FontAwesomeIcon v-if="value.value === 'income'" icon='far fa-arrow-alt-from-right' class='text-green-500' fixed-width aria-hidden='true' />
                                            <FontAwesomeIcon v-else icon='far fa-arrow-alt-from-left' class='text-red-500' fixed-width aria-hidden='true' />
                                        </div>
                                    </template>

                                    <template #option="{ option, isSelected, isPointed, search }">
                                        <div >
                                            {{ option.label }}
                                            <FontAwesomeIcon v-if="option.value === 'income'" icon='far fa-arrow-alt-from-right' class='text-green-500' fixed-width aria-hidden='true' />
                                            <FontAwesomeIcon v-else icon='far fa-arrow-alt-from-left' class='text-red-500' fixed-width aria-hidden='true' />
                                        </div>
                                    </template>
                                </PureMultiselect>
                            </div>
                        </template>
                    </Column>

                    <Column field="payment" header="Pembayaran">
                        <template #body="{ data, field }">
                            <div class="w-44">
                                <PureMultiselect
                                    v-model="data[field]"
                                    :options="enumPaymentFee"
                                    labelProp="label"
                                    valueProp="value"
                                    placeholder="Pilih cash/transfer"
                                    @update:modelValue="() => onRowChange(data)"
                                    :pt="{
                                        root: {
                                        class: isDirty(data.id, field)
                                            ? '!border !border-green-500 !bg-green-100'
                                            : ''
                                        }
                                    }"
                                />
                            </div>
                        </template>
                    </Column>
            
                    <Column field="amount" header="Jumlah">
                        <template #body="{ data, field }">
                            <InputNumber
                                v-model="data[field]"
                                :useGrouping="true"
                                @update:modelValue="() => onRowChange(data)"
                                currency="idr"
                                mode="currency"
                                locale="id-ID"
                                :prefix="data.type === 'expense' ? '-' : ''"
                                :maxFractionDigits="0"
                                :inputClass="[
                                    isDirty(data.id, field)
                                    ? '!border !border-green-500 !bg-green-100'
                                    : '',
                                    'w-32'
                                ]"
                            />
                        </template>
                    </Column>
            
                    <Column field="amount" header="Aksi">
                        <template #body="{ data, field }">
                            <Button icon="fal fa-trash-alt" type="negative" @click="onDeleteCost(data.id)" />
                        </template>
                    </Column>
                </DataTable>
            </div>
            <Button
                label="Tambah biaya baru"
                type="dashed"
                full
                @click="addRow"
            />

        </div>
        
    </div>
</template>


